<template>
  <v-container class="contact-page" fluid>
    <v-row>
      <v-col>
        <v-breadcrumbs :items="breadcrumbs" divider=">" class="px-0 pb-0"></v-breadcrumbs>
      </v-col>
    </v-row>

    <div class="head-wrap mb-4">
      <div>
        <h1 class="headline mb-1">Contact Form <span class="thai">ข้อมูลการติดต่อ</span></h1>
        <p v-if="canViewMessageTab" class="subhead mb-0">Manage inquiries, feedback and complaints submitted from hospital branch websites.</p>
      </div>
      <v-btn v-if="canViewRoutingTab" class="setup-btn" outlined @click="goToRoutingTab">
        <v-icon left small>mdi-cog-outline</v-icon>
        Email Routing Setup
      </v-btn>
    </div>

    <div class="tabs mb-4">
      <button
        v-if="canViewMessageTab"
        :class="['tab', {'active': isSubmissionsTab}]"
      >
        Submissions
      </button>
      <button
        v-if="canViewRoutingTab"
        :class="['tab', {'active': isRoutingTab}]"
        @click="goToRoutingTab"
      >
        Email Routing Configuration
      </button>
      <button
        v-if="canViewHospitalContactInfoTab"
        :class="['tab', {'active': isHospitalInfoTab}]"
        @click="goToHospitalInfoTab"
      >
        Hospital Contact Info
      </button>
    </div>

    <v-card class="table-card">
      <div class="filter-bar">
        <div class="search-wrap">
          <v-text-field
            v-model="searchPhase"
            append-icon="mdi-magnify"
            outlined
            dense
            hide-details
            label="Search by Sender Name or Branch..."
            @keyup.enter="textSearch(searchPhase)"
            @click:clear="clearSearch"
            clearable
          ></v-text-field>
        </div>
        <v-select v-model="orgCode" :items="orgCodeItems" dense outlined hide-details item-text="text" item-value="value" class="small-select" :disabled="isInitialLoading || isBranchDisabled" @input="handleFilterChange"></v-select>
        <v-select v-model="formType" :items="formTypeItems" dense outlined hide-details item-text="text" item-value="value" class="small-select" :disabled="isInitialLoading" @input="handleFilterChange"></v-select>
        <v-select v-model="status" :items="statusItems" dense outlined hide-details item-text="text" item-value="value" class="small-select" :disabled="isInitialLoading" @input="handleFilterChange"></v-select>
        <v-btn
          outlined
          class="filter-btn"
          :class="{'has-filtered': hasAppliedFilters}"
          @click="handleFilterButtonClick"
        >
          <v-icon small left>{{ hasAppliedFilters ? 'mdi-close' : 'mdi-filter-variant' }}</v-icon>
          {{ hasAppliedFilters ? 'Clear' : 'Filter' }}
        </v-btn>
      </div>

      <v-data-table
        :loading="isLoading || isTableLoading || isInitialLoading"
        :options.sync="options"
        :headers="table.headers"
        :items="list"
        :items-per-page="table.pageSize"
        :server-items-length="total"
        hide-default-footer
        class="contact-table"
        @click:row="handleRowClick"
      >
        <template v-slot:item.no="{index, item}">
          {{ rowNo(index, item) }}
        </template>
        <template v-slot:item.created_at="{item}">
          {{ dateFmt(item.created_at) }}
        </template>
        <template v-slot:item.org_codename="{item}">
          <span class="branch-tag">
            {{ branchLabel(item) }}
          </span>
        </template>
        <template v-slot:item.first_name="{item}">
          {{ item.first_name || '' }}
        </template>
        <template v-slot:item.last_name="{item}">
          {{ item.last_name || '' }}
        </template>
        <template v-slot:item.locale="{item}">
          <span class="locale-pill">{{ localeLabel(item.locale) }}</span>
        </template>
        <template v-slot:item.form_type="{item}">
          <span class="type-pill">{{ capitalize(item.form_type) }}</span>
        </template>
        <template v-slot:item.status="{item}">
          <span class="status-pill" :class="statusClass(item.status)">
            <span class="dot"></span>{{ statusLabel(item.status) }}
          </span>
        </template>
        <template v-slot:item.action="{item}">
          <button class="icon-btn" @click.stop="openDialog(item)">
            <v-icon size="16">mdi-eye-outline</v-icon>
          </button>
        </template>
      </v-data-table>

      <div class="table-foot">
        <span>Showing {{ pageStart }} to {{ pageEnd }} of {{ total }} entries</span>
        <v-pagination v-model="paginationPage" :length="pageLength" :total-visible="7"></v-pagination>
      </div>
    </v-card>

    <v-dialog v-model="isDialogOpen" max-width="1160" persistent>
      <v-card v-if="selectedMessage" class="detail-modal">
        <v-card-title class="d-flex justify-space-between align-center detail-head">
          <div>
            <div class="detail-title">Submission #{{ selectedMessage.id.slice(0, 8).toUpperCase() }}</div>
            <div class="detail-sub">Contact form inquiry — {{ ((selectedMessage.org_codename || selectedMessage.org_code) || '').toUpperCase() }}</div>
          </div>
          <v-btn icon class="close-btn" @click="closeDialog"><v-icon>mdi-close</v-icon></v-btn>
        </v-card-title>

        <div class="detail-body">
          <div class="left-col">
            <div class="section-title">Inquiry Information</div>
            <div class="grid-2">
              <div><label>First Name</label><p>{{ selectedMessage.first_name || '-' }}</p></div>
              <div><label>Last Name</label><p>{{ selectedMessage.last_name || '-' }}</p></div>
              <div><label>Phone</label><p>{{ selectedMessage.phone }}</p></div>
              <div><label>Email</label><p>{{ selectedMessage.email }}</p></div>
              <div><label>Submitted</label><p>{{ dateFmt(selectedMessage.created_at) }}</p></div>
              <div><label>Locale</label><p><span class="locale-badge">{{ localeLabel(selectedMessage.locale) }}</span></p></div>
            </div>

            <div class="mt-2">
              <label>Source Page</label>
              <p v-if="selectedMessage.source_page" class="link-text">
                <a :href="selectedMessage.source_page" target="_blank" rel="noopener noreferrer">{{ selectedMessage.source_page }}</a>
              </p>
              <p v-else>-</p>
            </div>
            <div class="mt-2"><label>Question Type / ประเภทของคำถาม</label><p><span class="type-pill">{{ questionTypeLabel(selectedMessage.form_type) }}</span></p></div>

            <hr class="sep" />
            <div class="pdpa-audit">
              <div class="pdpa-audit-head">
                <v-icon small>mdi-lock-outline</v-icon>
                PDPA Legal Audit Trail
              </div>
              <div class="pdpa-audit-grid">
                <div class="audit-item">
                  <label>Consent Status</label>
                  <p>
                    <span class="consent-chip" :class="selectedMessage.consent_accepted ? 'accepted' : 'rejected'">
                      <v-icon x-small class="consent-icon">{{ selectedMessage.consent_accepted ? 'mdi-check' : 'mdi-close' }}</v-icon>
                      {{ selectedMessage.consent_accepted ? 'Accepted' : 'Not Accepted' }}
                    </span>
                  </p>
                </div>
                <div class="audit-item"><label>Policy Version</label><p class="mono">{{ selectedMessage.consent_policy_version || '-' }}</p></div>
                <div class="audit-item"><label>Submission Timestamp</label><p class="mono">{{ pdpaTimestampFmt(selectedMessage.created_at) }}</p></div>
                <div class="audit-item"><label>Captured IP Address</label><p class="mono">{{ selectedMessage.user_ip || '-' }}</p></div>
                <div class="audit-item span-2"><label>User Agent</label><p class="mono ua-text">{{ selectedMessage.user_agent || '-' }}</p></div>
              </div>
              <div class="audit-footnote">Captured automatically at submission for PDPA legal proof of consent. Read-only.</div>
            </div>

            <hr class="sep" />
            <label>Message Content</label>
            <div class="message-box">{{ selectedMessage.message }}</div>
          </div>

          <div class="right-col">
            <div class="section-title">Admin Case Actions</div>
            <label>Case Status</label>
            <v-select
              v-model="detailStatus"
              :items="detailStatusItems"
              item-text="text"
              item-value="value"
              dense
              outlined
              hide-details
              :disabled="!canEditMessage"
            ></v-select>

            <label class="mt-4">Admin Notes (บันทึกภายใน)</label>
            <quill-editor
              ref="adminNotesEditor"
              v-model="detailNotes"
              :options="adminNotesEditorOption"
              class="admin-notes-editor"
            ></quill-editor>
            <div class="hint-text">Visible to admin staff only. Not shared with sender.</div>

            <div class="section-title mt-5">Activity</div>
            <div v-if="isActivityLoading || !isActivityLoaded" class="activity-loading">
              <v-progress-circular indeterminate size="18" width="2" color="primary"></v-progress-circular>
              <span>Loading activity history...</span>
            </div>
            <div v-for="log in activityLogs" :key="log.id" class="activity-item">
              <span class="bullet blue"></span>
              <div class="activity-content">
                <div class="act-title">Status changed to {{ statusLabel(log.to_status) }} by {{ log.updated_by_name}}</div>
                <div v-if="isNoteOpen(`log-${log.id}`)" class="act-note">{{ 'Note: ' + notePreview(log.admin_notes) }}</div>
                <button
                  v-if="hasNoteContent(log.admin_notes)"
                  type="button"
                  class="act-note-toggle"
                  @click="toggleNote(`log-${log.id}`)"
                >
                  {{ isNoteOpen(`log-${log.id}`) ? 'Hide note' : 'Show note' }}
                </button>
                <div class="act-time">{{ activityTimeFmt(log.created_at) }}</div>
                <hr class="activity-sep" />
              </div>
            </div>
            <div class="activity-item" v-if="selectedMessage && isActivityLoaded">
              <span class="bullet gray"></span>
              <div class="activity-content">
                <div class="act-title">Submission received {{ statusLabel(selectedMessage.status) }}</div>
                <button
                  v-if="hasNoteContent(selectedMessage.admin_notes)"
                  type="button"
                  class="act-note-toggle"
                  @click="toggleNote('submission')"
                >
                  {{ isNoteOpen('submission') ? 'Hide note' : 'Show note' }}
                </button>
                <div v-if="isNoteOpen('submission')" class="act-note">{{ 'Note: ' + notePreview(selectedMessage.admin_notes) }}</div>
                <div class="act-time">{{ activityTimeFmt(selectedMessage.created_at) }}</div>
              </div>
            </div>
          </div>
        </div>

        <v-card-actions class="detail-foot">
          <v-spacer></v-spacer>
          <v-btn outlined @click="closeDialog">Cancel</v-btn>
          <v-btn v-if="canEditMessage" color="primary" :loading="isSaveLoading" :disabled="!canSaveDetail" @click="saveDetail">Save Changes</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import moment from 'moment'
import {mapState, mapActions} from 'vuex'
import {searchHandler, hasPermision} from '@/helper/helper'
import client from '@/apis/http-client'
import 'quill/dist/quill.core.css'
import 'quill/dist/quill.snow.css'
import 'quill/dist/quill.bubble.css'
import {quillEditor} from 'vue-quill-editor'

export default {
  components: {
    quillEditor
  },
  data() {
    return {
      searchPhase: '',
      options: {},
      timer: null,
      isInitialLoading: false,
      isTableLoading: false,
      isDialogOpen: false,
      isSaveLoading: false,
      isActivityLoaded: false,
      openNoteKeys: [],
      detailStatus: 'new',
      detailNotes: '',
      formType: '',
      status: '',
      orgCode: '',
      orgPermissionItems: [],
      paginationPage: 1,
      table: {
        pageSize: 10,
        headers: [
          {text: 'NO.', value: 'no', sortable: false, width: 70},
          {text: 'DATE / TIME', value: 'created_at', sortable: false, width: 170},
          {text: 'BRANCH', value: 'org_codename', sortable: false, width: 140},
          {text: 'FIRST NAME', value: 'first_name', sortable: false},
          {text: 'LAST NAME', value: 'last_name', sortable: false},
          {text: 'LOCALE', value: 'locale', sortable: false, width: 100},
          {text: 'FORM TYPE', value: 'form_type', sortable: false, width: 150},
          {text: 'STATUS', value: 'status', sortable: false, width: 160},
          {text: 'ACTIONS', value: 'action', sortable: false, width: 100, align: 'center'}
        ]
      },
      formTypeItems: [{text: 'All Types', value: ''}],
      statusItems: [{text: 'All Status', value: ''}],
      detailStatusItems: [
        {text: 'New', value: 'new'},
        {text: 'Resolved', value: 'resolved'},
        {text: 'Sent', value: 'sent'},
        {text: 'Failed', value: 'failed'}
      ],
      orgCodeItems: [{text: 'All Branches', value: ''}],
      breadcrumbs: [
        {text: 'Dashboard', exact: true, href: '/'},
        {text: 'Contact Form', disabled: true, href: '/contact-form'}
      ]
    }
  },
  computed: {
    ...mapState('auth', ['user']),
    ...mapState('contactMessage', ['list', 'isLoading', 'isActivityLoading', 'total', 'selectedMessage', 'activityLogs', 'filterOptions']),
    isBranchDisabled() {
      const user = this.$store.state.auth.user || {}
      return !user.is_superuser
    },
    canViewMessageTab() {
      return hasPermision('CanViewContactMessage')
    },
    canViewRoutingTab() {
      return hasPermision('CanViewContactRoute')
    },
    canEditMessage() {
      return hasPermision('CanEditContactMessage')
    },
    canSaveDetail() {
      if (!this.canEditMessage || !this.selectedMessage) return false
      return this.detailStatus !== this.selectedMessage.status
    },
    isSubmissionsTab() {
      return this.$route.name === 'contact-form-list'
    },
    isRoutingTab() {
      return this.$route.name === 'contact-form-routing'
    },
    isHospitalInfoTab() {
      return this.$route.name === 'contact-form-hospital-info'
    },
    canViewHospitalContactInfoTab() {
      return hasPermision('CanViewOrganizationContact') || hasPermision('CanEditOrganizationContact')
    },
    pageLength() {
      return Math.max(1, Math.ceil((this.total || 0) / this.table.pageSize))
    },
    pageStart() {
      if (!this.total) return 0
      return (this.paginationPage - 1) * this.table.pageSize + 1
    },
    pageEnd() {
      return Math.min(this.paginationPage * this.table.pageSize, this.total)
    },
    activeFilterCount() {
      let count = 0
      if (this.formType) count += 1
      if (this.status) count += 1
      if (!this.isBranchDisabled && this.orgCode) count += 1
      if (this.searchPhase && this.searchPhase.trim()) count += 1
      return count
    },
    hasAppliedFilters() {
      return this.activeFilterCount > 0
    },
    currentTime() {
      return moment().format('HH:mm')
    },
    adminNotesEditorOption() {
      return {
        readOnly: !this.canEditMessage,
        placeholder: this.canEditMessage ? 'Type admin notes...' : '',
        modules: {
          toolbar: this.canEditMessage
            ? [['bold', 'italic', 'underline'], [{list: 'ordered'}, {list: 'bullet'}]]
            : false
        }
      }
    }
  },
  methods: {
    ...mapActions('contactMessage', [
      'fetch',
      'fetchFilterOptions',
      'updateMessage',
      'fetchActivityLogs',
      'setSelectedMessage',
      'setPage',
      'setPageSize',
      'setFieldSort',
      'setSort',
      'setTextSearch',
      'setFilterFormType',
      'setFilterStatus',
      'setFilterOrgCode',
      'resetList'
    ]),
    rowNo(index, item) {
      const page = Number(this.paginationPage) || 1
      const pageSize = Number(this.table.pageSize) || 10
      let rowIndex = Number(index)

      if (Number.isNaN(rowIndex)) {
        const fallbackIndex = this.list.findIndex((listItem) => {
          if (item && item.id && listItem.id) {
            return String(listItem.id) === String(item.id)
          }
          return listItem === item
        })
        rowIndex = fallbackIndex >= 0 ? fallbackIndex : 0
      }

      return (page - 1) * pageSize + rowIndex + 1
    },
    getUserOrgCode() {
      const code = this.user && (this.user.org_code || this.user.org_codename || this.user.org)
      return (code || '').toLowerCase()
    },
    dateFmt(date) {
      return moment(date).format('DD/MM/YYYY HH:mm')
    },
    activityTimeFmt(date) {
      if (!date) return '-'
      const value = moment(date)
      if (value.isSame(moment(), 'day')) {
        return `Today, ${value.format('HH:mm')}`
      }
      if (value.isSame(moment().subtract(1, 'day'), 'day')) {
        return `Yesterday, ${value.format('HH:mm')}`
      }
      return value.format('DD/MM/YYYY HH:mm')
    },
    pdpaTimestampFmt(date) {
      if (!date) return '-'
      return moment(date).format('YYYY-MM-DD HH:mm:ss')
    },
    toggleNote(key) {
      if (this.openNoteKeys.includes(key)) {
        this.openNoteKeys = this.openNoteKeys.filter((item) => item !== key)
        return
      }
      this.openNoteKeys = [...this.openNoteKeys, key]
    },
    stripHtml(value) {
      if (!value) return ''
      const textOnly = String(value)
        .replace(/<br\s*\/?>/gi, ' ')
        .replace(/<[^>]*>/g, ' ')
        .replace(/&nbsp;/gi, ' ')
      return textOnly.replace(/\s+/g, ' ').trim()
    },
    hasNoteContent(value) {
      return Boolean(this.stripHtml(value))
    },
    notePreview(value) {
      return this.stripHtml(value) || '-'
    },
    isNoteOpen(key) {
      return this.openNoteKeys.includes(key)
    },
    capitalize(text) {
      if (!text) return '-'
      return text.replace('_', ' ').replace(/\b\w/g, char => char.toUpperCase())
    },
    statusLabel(status) {
      const map = {
        new: 'New',
        in_progress: 'In Progress',
        resolved: 'Resolved',
        sent: 'Sent',
        failed: 'Failed'
      }
      return map[status] || this.capitalize(status)
    },
    statusClass(status) {
      const map = {
        new: 'status-red',
        in_progress: 'status-orange',
        resolved: 'status-green',
        sent: 'status-orange',
        failed: 'status-red'
      }
      return map[status] || 'status-gray'
    },
    senderName(item = {}) {
      const firstName = item.first_name || ''
      const lastName = item.last_name || ''
      const combined = `${firstName} ${lastName}`.trim()
      return combined || item.full_name || '-'
    },
    branchLabel(item = {}) {
      const value = (item.org_codename || item.org_code || '').trim()
      return value ? value.toUpperCase() : ''
    },
    localeLabel(locale) {
      return (locale || 'th').toUpperCase()
    },
    async loadOrgItems() {
      const response = await client.secureHttpClient.get('por/api/organization/permission?permission=View&category=Organization')
      const orgs = response?.data?.data || []
      this.orgPermissionItems = orgs.map(org => ({
        text: `${org.name || org.name_th || org.codename} (${org.codename})`,
        value: org.codename
      }))
    },
    questionTypeLabel(formType) {
      const typeMap = {
        contact: 'สอบถามข้อมูลการรักษา (Treatment Inquiry)',
        feedback: 'ข้อเสนอแนะ (Feedback)',
        complaint: 'ร้องเรียน (Complaint)'
      }
      return typeMap[formType] || this.capitalize(formType)
    },
    clearSearch() {
      this.searchPhase = ''
      this.setTextSearch('')
      this.paginationPage = 1
      this.fetchByCurrent()
    },
    textSearch(value) {
      this.searchPhase = value
      this.timer = searchHandler(this.timer, clearTimeout, async () => {
        this.setTextSearch(this.searchPhase.trim())
        this.paginationPage = 1
        await this.fetchByCurrent()
      })
    },
    async handleFilterChange() {
      this.setFilterFormType(this.formType)
      this.setFilterStatus(this.status)
      this.setFilterOrgCode(this.orgCode)
      this.paginationPage = 1
      await this.fetchByCurrent()
    },
    async clearFilters() {
      this.formType = ''
      this.status = ''
      this.searchPhase = ''
      if (!this.isBranchDisabled) {
        this.orgCode = ''
      }
      this.setTextSearch('')
      await this.handleFilterChange()
    },
    async handleFilterButtonClick() {
      if (this.hasAppliedFilters) {
        await this.clearFilters()
        return
      }
      await this.handleFilterChange()
    },
    goToRoutingTab() {
      this.$router.push({name: 'contact-form-routing'}).catch(() => {})
    },
    goToHospitalInfoTab() {
      this.$router.push({name: 'contact-form-hospital-info'}).catch(() => {})
    },
    async openDialog(item) {
      this.setSelectedMessage(item)
      this.detailStatus = item.status || 'new'
      this.detailNotes = item.admin_notes || ''
      this.isActivityLoaded = false
      this.openNoteKeys = []
      this.$store.commit('contactMessage/SET_IS_ACTIVITY_LOADING', true)
      this.$store.commit('contactMessage/SET_CURRENT_ACTIVITY_MESSAGE_ID', item.id)
      this.isDialogOpen = true
      this.$store.commit('contactMessage/SET_ACTIVITY_LOGS', [])
      await this.fetchActivityLogs(item.id)
      this.isActivityLoaded = true
      this.$nextTick(() => {
        if (this.$refs.adminNotesEditor && this.$refs.adminNotesEditor.quill) {
          this.$refs.adminNotesEditor.quill.enable(this.canEditMessage)
        }
      })
    },
    handleRowClick(item) {
      this.openDialog(item)
    },
    closeDialog() {
      this.isDialogOpen = false
      this.isActivityLoaded = false
      this.openNoteKeys = []
      this.detailStatus = 'new'
      this.detailNotes = ''
      this.$store.commit('contactMessage/SET_IS_ACTIVITY_LOADING', false)
    },
    async saveDetail() {
      if (!this.selectedMessage) return
      if (!this.canSaveDetail) return
      this.isSaveLoading = true
      const messageId = this.selectedMessage.id
      const ok = await this.updateMessage({
        id: messageId,
        status: this.detailStatus,
        admin_notes: this.detailNotes
      })
      this.isSaveLoading = false
      if (ok) {
        this.closeDialog()
        await this.fetchByCurrent()
      }
    },
    async fetchByCurrent() {
      this.isTableLoading = true
      try {
        this.setPage(this.paginationPage)
        this.setPageSize(this.table.pageSize)
        await this.fetch()
      } finally {
        this.isTableLoading = false
      }
    },
    loadFilterOptions() {
      const orgOptions = this.orgPermissionItems.length
        ? this.orgPermissionItems
        : (this.filterOptions.org_codenames || this.filterOptions.org_codes || []).map((org) => ({
          text: (org || '').toUpperCase(),
          value: org
        }))
      const typeValues = (this.filterOptions.form_types && this.filterOptions.form_types.length)
        ? this.filterOptions.form_types
        : []
      const typeOptions = typeValues.map((type) => ({
        text: this.capitalize(type),
        value: type
      }))
      const statusValues = (this.filterOptions.statuses && this.filterOptions.statuses.length)
        ? this.filterOptions.statuses
        : []
      const statusOptions = statusValues.map((status) => ({
        text: this.statusLabel(status),
        value: status
      }))

      if (this.isBranchDisabled) {
        const userOrgCode = this.getUserOrgCode()
        this.orgCode = userOrgCode
        this.setFilterOrgCode(userOrgCode)
        this.orgCodeItems = [{text: (userOrgCode || '-').toUpperCase(), value: userOrgCode}]
      } else {
        this.orgCodeItems = [{text: 'All Branches', value: ''}, ...orgOptions]
      }
      this.formTypeItems = [{text: 'All Types', value: ''}, ...typeOptions]
      this.statusItems = [{text: 'All Status', value: ''}, ...statusOptions]
    }
  },
  async created() {
    if (!this.canViewMessageTab && this.canViewRoutingTab) {
      this.$router.replace({name: 'contact-form-routing'}).catch(() => {})
      return
    }
    if (!this.canViewMessageTab && !this.canViewRoutingTab && this.canViewHospitalContactInfoTab) {
      this.$router.replace({name: 'contact-form-hospital-info'}).catch(() => {})
      return
    }
    if (!this.canViewMessageTab && !this.canViewRoutingTab) {
      this.$router.replace({name: 'not-found'}).catch(() => {})
      return
    }

    this.isInitialLoading = true

    await Promise.allSettled([
      (async () => {
        try {
          await this.loadOrgItems()
        } catch (e) {
          this.orgPermissionItems = []
        }
      })(),
      (async () => {
        try {
          await this.fetchFilterOptions()
        } catch (e) {
          // noop
        }
      })()
    ])

    if (this.isBranchDisabled) {
      this.orgCode = this.getUserOrgCode()
      this.setFilterOrgCode(this.orgCode)
    }

    this.loadFilterOptions()

    await this.fetchByCurrent()
    this.isInitialLoading = false
  },
  beforeRouteLeave(to, from, next) {
    this.resetList()
    next()
  },
  watch: {
    filterOptions: {
      handler() {
        this.loadFilterOptions()
      },
      deep: true
    },
    paginationPage() {
      this.fetchByCurrent()
    }
  }
}
</script>

<style scoped>
.contact-page { padding: 8px 24px 40px; }
.head-wrap { display:flex; justify-content:space-between; align-items:flex-start; gap:16px; }
.thai { font-size:15px; color:#64748b; font-weight:500; }
.subhead { color:#64748b; font-size:13px; }
.setup-btn { border-color:#e2e8f0 !important; text-transform:none; font-weight:600; }
.tabs { display:flex; border-bottom:1px solid #e6e9f0; }
.tab { border:none; background:transparent; padding:10px 18px; font-weight:600; color:#64748b; cursor:pointer; }
.tab.active { color:#2f6fed; border-bottom:2px solid #2f6fed; }
.table-card { border:1px solid #e6e9f0; border-radius:10px !important; overflow:hidden; }
.filter-bar { display:flex; gap:10px; padding:14px; align-items:center; border-bottom:1px solid #eef2f7; }
.search-wrap { flex:1; }
.small-select { max-width:150px; }
.branch-scope { min-width:150px; max-width:200px; height:40px; display:flex; align-items:center; padding:0 12px; border:1px solid #e2e8f0; border-radius:4px; color:#475569; background:#f8fafc; font-size:13px; font-weight:600; }
.filter-btn { text-transform:none; font-weight:600; border-color:#e2e8f0 !important; }
.filter-btn.has-filtered { color:#ef4444 !important; border-color:#fca5a5 !important; background-color:#fef2f2 !important; }
.contact-table::v-deep th { font-size:12px !important; color:#64748b !important; }
.contact-table::v-deep td { font-size:14px; }
.type-pill { display:inline-flex; background:#eef4ff; color:#2f6fed; border-radius:7px; padding:6px 10px; font-size:12px; font-weight:700; line-height:1.2; }
.locale-pill { background:#f1f5f9; color:#334155; border-radius:999px; padding:2px 8px; font-size:11px; font-weight:700; }
.locale-badge { display:inline-flex; align-items:center; justify-content:center; min-width:28px; height:20px; border-radius:5px; font-size:10.5px; font-weight:700; letter-spacing:.3px; background:#eef4ff; color:#1e4488; border:1px solid #d7e6ff; }
.branch-tag { display:inline-flex; align-items:center; font-size:11.5px; font-weight:700; letter-spacing:.2px; padding:4px 9px; border-radius:6px; background:#eef1f6; color:#5b6472; }
.branch-tag.central { background:#f2ecfc; color:#7c3aed; }
.status-pill { display:inline-flex; align-items:center; gap:7px; padding:4px 10px; border-radius:12px; font-weight:700; font-size:12px; }
.status-pill .dot { width:6px; height:6px; border-radius:50%; }
.status-pill.status-green { background:#f0fdf4; color:#15803d; }
.status-pill.status-green .dot { background:#1aa964; }
.status-pill.status-orange { background:#fffbeb; color:#b45309; }
.status-pill.status-orange .dot { background:#f59e0b; }
.status-pill.status-red { background:#fef2f2; color:#b91c1c; }
.status-pill.status-red .dot { background:#ef4444; }
.status-pill.status-gray { background:#f8fafc; color:#64748b; }
.status-pill.status-gray .dot { background:#94a3b8; }
.icon-btn { width:30px; height:30px; border-radius:7px; border:1px solid #e6e9f0; background:#fff; }
.table-foot { display:flex; align-items:center; justify-content:space-between; padding:14px 16px; font-size:12px; color:#64748b; }
.detail-modal { border-radius:14px !important; overflow:hidden; display:flex; flex-direction:column; max-height:88vh; }
.detail-head { border-bottom:1px solid #e6e9f0; padding:16px 20px; }
.detail-title { font-size:28px; font-weight:700; line-height:1.1; }
.detail-sub { font-size:13px; color:#64748b; line-height:1.2; }
.close-btn { background:#f1f5f9; }
.detail-body { display:grid; grid-template-columns: 1.15fr 1fr; min-height:460px; flex:1; min-height:0; overflow:hidden; }
.left-col, .right-col { padding:18px 20px; min-width:0; }
.left-col { overflow-y:auto; min-height:0; }
.left-col { border-right:1px solid #e6e9f0; background:#fbfcfe; }
.right-col { overflow:hidden; }
.section-title { text-transform:uppercase; font-size:12px; color:#94a3b8; font-weight:800; margin-bottom:10px; letter-spacing:.5px; }
.grid-2 { display:grid; grid-template-columns:1fr 1fr; gap:10px 14px; }
label { display:block; font-size:10.5px; color:#64748b; text-transform:uppercase; font-weight:700; margin-bottom:5px; letter-spacing:.4px; }
p { margin:0; font-size:13px; font-weight:600; color:#0f172a; line-height:1.35; }
.link-text { color:#2f6fed; word-break:break-word; }
.sep { border:none; border-top:1px solid #e6e9f0; margin:14px 0; }
.message-box { border:1px solid #e6e9f0; border-radius:8px; background:#fff; padding:12px; line-height:1.5; font-size:13px; font-weight:500; color:#0f172a; }
.pdpa-audit { border:1px solid #dbeafe; background:#f8fbff; border-radius:10px; padding:12px; }
.pdpa-audit-head { display:flex; align-items:center; gap:6px; font-size:13px; font-weight:700; color:#1e3a8a; margin-bottom:10px; }
.pdpa-audit-head .v-icon { color:#1e3a8a !important; }
.pdpa-audit-grid { display:grid; grid-template-columns:1fr 1fr; gap:10px 14px; }
.audit-item { min-width:0; }
.consent-chip { display:inline-flex; align-items:center; gap:6px; font-size:12px; font-weight:700; border-radius:999px; padding:5px 10px; }
.consent-chip.accepted { background:#ecfdf3; color:#15803d; }
.consent-chip.rejected { background:#fef2f2; color:#b91c1c; }
.consent-chip.accepted .consent-icon { color:#15803d !important; }
.consent-chip.rejected .consent-icon { color:#b91c1c !important; }
.span-2 { grid-column: 1 / -1; }
.ua-text { word-break:break-word; }
.mono { font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, 'Liberation Mono', 'Courier New', monospace; font-size:12px; font-weight:600; }
.audit-footnote { margin-top:10px; padding-top:10px; border-top:1px dashed #cfe0ff; font-size:11px; color:#8ea0be; }
.admin-notes-editor { border:1px solid #e6e9f0; border-radius:8px; background:#fff; }
.admin-notes-editor ::v-deep .ql-container { min-height: 150px; }
.hint-text { font-size:11px; color:#94a3b8; margin-top:6px; }
.activity-item { display:flex; gap:10px; margin-bottom:12px; }
.activity-content { flex:1; min-width:0; }
.bullet { width:9px; height:9px; border-radius:50%; margin-top:5px; }
.bullet.blue { background:#2563eb; }
.bullet.gray { background:#94a3b8; }
.act-title { font-size:13px; font-weight:600; }
.act-time { font-size:11px; color:#94a3b8; }
.act-note-toggle { border:none; background:transparent; color:#2563eb; font-size:11px; padding:0; margin-top:2px; cursor:pointer; }
.act-note { font-size:12px; color:#475569; margin-top:2px; }
.activity-sep { border:none; border-top:1px solid #e6e9f0; margin:8px 0 0; width:100%; display:block; }
.activity-loading { display:flex; align-items:center; gap:8px; color:#64748b; font-size:12px; margin-bottom:10px; }
.activity-empty { color:#94a3b8; font-size:12px; margin-bottom:10px; }
.detail-foot { border-top:1px solid #e6e9f0; padding:14px 20px; flex-shrink:0; }
</style>
